import {
  BN,
  isNormalPositive,
} from '@bifrost-platform/bifront-sdk-react-biholder';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import styled from 'styled-components';
import LTVVisualizeBar from '@/components/DashBoard/LTVVisualizeBar';
import { FlexBox } from '@/components/atom/FlexBox';
import { StyledText } from '@/components/atom/Text';
import { LIQUIDATION_RATIO, MAX_LTV_RATIO } from '@/configs/value';
import useBtcfi from '@/hooks/api/useBtcfi';
import useGetCurrencyString from '@/hooks/store/useGetCurrencyString';
import { formatNumber, formatRate } from '@/libs/formatNumber';

const TextContainer = styled(FlexBox)`
  & span {
    ${(p) => p.theme.text.xs};
    white-space: pre-line;
  }
`;

interface Props {}

export default function LTVDetailInfo({}: Props) {
  // translate
  const { t } = useTranslation('dashboard');

  // currency
  const { getNumberWithCurrencyReplace } = useGetCurrencyString();

  // btcfi
  const { assetSets, borrowAmountValue, depositAmountValue, ltv } = useBtcfi();

  // memo
  const totalDepositAmount = useMemo(
    () =>
      assetSets.reduce((pre, cur) => pre.plus(cur.depositAmount), new BN(0)),
    [assetSets]
  );
  const liquidationPrice = useMemo(
    () =>
      isNormalPositive(totalDepositAmount, true)
        ? formatNumber(
            new BN(depositAmountValue)
              .div(totalDepositAmount)
              .multipliedBy(new BN(ltv).div(100))
          )
        : '0',
    [depositAmountValue, ltv, totalDepositAmount]
  );
  const currentLTV = useMemo(
    () => Math.min(new BN(ltv).toNumber(), 100),
    [ltv]
  );

  return (
    <FlexBox $flex $direction="column" $gap="xs">
      <TextContainer $flex $direction="column" $gap="md">
        <FlexBox $flex $direction="column" $gap="xxxs">
          <StyledText $fontWeight="medium">{`${t('currentLoan')}: ${getNumberWithCurrencyReplace(borrowAmountValue)}`}</StyledText>
          <StyledText $fontWeight="medium">{`${t('providedSecurity')}: ${getNumberWithCurrencyReplace(depositAmountValue)}`}</StyledText>
          <StyledText $fontWeight="medium">{`${t('ltv')}: ${formatRate(ltv)}%`}</StyledText>
          <StyledText $fontWeight="medium">{`${t('liquidationPrice')}: ${getNumberWithCurrencyReplace(liquidationPrice)}`}</StyledText>
        </FlexBox>
        <span>{t('ltvDescription')}</span>
      </TextContainer>
      <LTVVisualizeBar
        currentLTV={currentLTV}
        maxLTV={MAX_LTV_RATIO}
        liquidation={LIQUIDATION_RATIO}
      />
    </FlexBox>
  );
}
